import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Link,
  Divider,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import {
  GetApp,
  PictureAsPdf,
  InsertDriveFile,
  OpenInFull,
  Close,
} from "@mui/icons-material";
import { AssignmentSubmission } from "../../types";

export interface Props {
  submission?: AssignmentSubmission;
  authorName?: string;
}

export const AssignmentSubmissionPreview: React.FC<Props> = ({
  submission,
  authorName = "Student",
}) => {
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  if (!submission) {
    return (
      <Card>
        <CardHeader title="Assignment Submission" />
        <CardContent>
          <Typography color="text.secondary">
            No assignment submission found for this student.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (extension === "pdf") {
      return <PictureAsPdf sx={{ color: "error.main", mr: 1, fontSize: 20 }} />;
    }
    return (
      <InsertDriveFile sx={{ color: "primary.main", mr: 1, fontSize: 20 }} />
    );
  };

  const getFileNameFromUrl = (url: string) => {
    // Extract filename from URL - handle both direct filenames and S3-style paths
    const parts = url.split("/");
    const filename = parts[parts.length - 1];

    // If the filename has URL parameters, remove them
    const cleanFilename = filename.split("?")[0];

    // If we can't determine a good filename, create a generic one
    if (!cleanFilename || cleanFilename.length < 3) {
      const extension = url.includes(".pdf")
        ? "pdf"
        : url.includes(".doc")
          ? "doc"
          : "file";
      return `submission_file.${extension}`;
    }

    return decodeURIComponent(cleanFilename);
  };

  const renderFilePreview = (fileUrl: string, fileName: string) => {
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    if (fileExtension === "pdf") {
      return (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {getFileIcon(fileName)}
              Attachment
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                onClick={() => setFullscreenOpen(true)}
                startIcon={<OpenInFull />}
                size="small"
              >
                Full Screen
              </Button>
              <Link
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <GetApp sx={{ mr: 1 }} />
                Download
              </Link>
            </Box>
          </Box>
          <Box
            sx={{
              height: 500,
              width: "100%",
              border: "1px solid #ddd",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <iframe
              src={fileUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          </Box>
        </Box>
      );
    } else {
      // For non-PDF files, show a placeholder with download option
      return (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {getFileIcon(fileName)}
              Attachment
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                onClick={() => setFullscreenOpen(true)}
                startIcon={<OpenInFull />}
                size="small"
              >
                Full Screen
              </Button>
              <Link
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <GetApp sx={{ mr: 1 }} />
                Download
              </Link>
            </Box>
          </Box>
          <Box
            sx={{
              height: 200,
              bgcolor: "#f5f5f5",
              border: "1px solid #ddd",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {getFileIcon(fileName)}
            <Typography variant="body1" sx={{ mb: 1, textAlign: "center" }}>
              {fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
              {fileExtension?.toUpperCase()} file - Click download to view
            </Typography>
          </Box>
        </Box>
      );
    }
  };

  const renderFullscreenContent = (fileUrl: string, fileName: string) => {
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    if (fileExtension === "pdf") {
      return (
        <Box sx={{ position: "fixed", top: 64, left: 0, right: 0, bottom: 0 }}>
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Fullscreen Preview"
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: "fixed",
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 800,
            width: "100%",
            border: "1px solid #ddd",
            borderRadius: 1,
            bgcolor: "#f5f5f5",
            p: 4,
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>{getFileIcon(fileName)}</Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {fileName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Preview is not available for this file type. Use the download link
            to view the file.
          </Typography>
          <Button
            variant="contained"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<GetApp />}
          >
            Download
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {submission.submissionText && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="blockquote"
                sx={{
                  p: 2,
                  pl: 3,
                  borderLeft: (theme) =>
                    `4px solid ${theme.palette.primary.light}`,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  whiteSpace: "pre-wrap",
                  fontStyle: "italic",
                  lineHeight: 1.8,
                  m: 0,
                }}
              >
                {submission.submissionText}
              </Box>
            </Box>
          )}

          {submission.submissionFileUrl && (
            <>
              {submission.submissionText && <Divider sx={{ my: 2 }} />}
              {renderFilePreview(
                submission.submissionFileUrl,
                getFileNameFromUrl(submission.submissionFileUrl),
              )}
            </>
          )}

          {!submission.submissionText && !submission.submissionFileUrl && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                This student has not submitted any content yet.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      {submission.submissionFileUrl && (
        <Dialog
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          fullScreen
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setFullscreenOpen(false)}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {getFileNameFromUrl(submission.submissionFileUrl)}
              </Typography>
              <Button
                color="inherit"
                href={submission.submissionFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<GetApp />}
              >
                Download
              </Button>
            </Toolbar>
          </AppBar>
          {renderFullscreenContent(
            submission.submissionFileUrl,
            getFileNameFromUrl(submission.submissionFileUrl),
          )}
        </Dialog>
      )}
    </Box>
  );
};
